import axios, { AxiosResponse, AxiosRequestConfig } from 'axios';
import { ProductInfoContext } from 'components/Layout';
import { useContext, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  getAuthToken,
  deleteOrderFromStore,
  getShippingMethodId,
  CART_EVENTS_NAMES,
} from 'utils/cartHelpers';
import { STORE_ROOT } from 'utils/constants/urls';
import { GetStateAbbreviations } from 'utils/stateAbbreviations';
import { AxiosErrorResponse } from 'utils/types';

import {
  Order,
  CheckoutData,
  PaymentDetails,
  LineItem,
  PSCLocation,
} from './types';

const statesData = GetStateAbbreviations({ includeNewYork: true });

export const formatAbbreviationToState = (abbreviation: string): string =>
  statesData.find((data) => data.id === abbreviation)?.value || '';

/**
 * Get original price of a line item
 *
 * @param lineItem LineItem Object
 *
 * @returns Number describing base price of a single unit of the line item or null if unavailable
 */
export const originalPrice = (lineItem: LineItem): number | null => {
  if (lineItem?.is_subscription) {
    // FIXME: this hook is not following the rules of hooks for being used.
    // Since this is already in prod, and to avoid introducing a new bug, we are disabling the eslint rule for now.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const productSlugArray = Object.values(useContext(ProductInfoContext));
    const masterProduct = productSlugArray?.find(
      (product) => product.name === lineItem.product_name,
    );
    return masterProduct?.price ? parseFloat(masterProduct.price) : null;
  } else {
    return lineItem?.base_price ? parseFloat(lineItem?.base_price) : null;
  }
};

/**
 * Cleans up the completed order by removing it from store
 */
export const cleanupOrder = (): void => {
  deleteOrderFromStore();
};

/**
 * Process order from Checkout page with specified payment method
 *
 * @param order Order Object
 * @param checkoutData Data from the checkout Form or payment service
 * @param paymentDetails Object containing payment-method-specific details
 *
 * @returns Promise that will resolve with a completed Order object
 */
export const processOrder = async (
  order: Order,
  checkoutData: CheckoutData,
  payment: PaymentDetails | null,
): Promise<AxiosResponse> => {
  try {
    const url = `${STORE_ROOT}/aapi/v2/orders/${order.id}/process_order`;
    const authToken = await getAuthToken();
    const headers = {
      Authorization: `Bearer ${authToken}`,
    };
    let data = {};

    // For PayPal subscription orders
    if (payment && payment.billing_agreement_id) {
      data = {
        synchronous: 'true',
        payment,
        ...checkoutData,
        // Billing agreement id is added here to match the old checkout process
        billing_agreement_id: payment.billing_agreement_id,
      };
    } else {
      // For all other orders
      data = {
        synchronous: 'true',
        payment,
        ...checkoutData,
      };
    }

    const requestOpts: AxiosRequestConfig = {
      data,
      headers,
      method: 'PUT',
      url: url,
    };

    return await axios(requestOpts);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getNearestLabs = async (
  zipCode: string,
): Promise<AxiosResponse> => {
  try {
    const url = `${STORE_ROOT}/aapi/v1/pscs/search/${zipCode}`;
    const authToken = await getAuthToken();
    const headers = {
      Authorization: `Bearer ${authToken}`,
    };

    const requestOpts: AxiosRequestConfig = {
      headers,
      method: 'GET',
      url: url,
    };

    return await axios(requestOpts);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const addVariantToOrder = async (
  variantId: number,
  quantity: number,
  orderId: number,
): Promise<AxiosResponse> => {
  const data = {
    quantity,
    variant_id: variantId,
  };
  try {
    const url = `${STORE_ROOT}/aapi/v1/orders/${orderId}/line_items?location=${window.location}`;
    const authToken = await getAuthToken();
    const requestOpts: AxiosRequestConfig = {
      data,
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      method: 'POST',
      url,
    };
    return await axios(requestOpts);
  } catch (error) {
    if ((error as AxiosErrorResponse).response?.status === 404) {
      cleanupOrder();
    }
    return Promise.reject(error);
  }
};

export const displayLineItemsQty = (
  lineItems: LineItem[] | undefined,
): number => {
  let totalQuantity;
  if (lineItems) {
    totalQuantity = lineItems?.reduce(function (accumulator, item) {
      return accumulator + item?.quantity;
    }, 0);
  }
  return totalQuantity || 0;
};

export const completeOrder = async (
  order: Order,
  checkoutData: CheckoutData,
  payment: PaymentDetails | null,
): Promise<AxiosResponse> => {
  try {
    const url = `${STORE_ROOT}/aapi/v2/orders/${order.id}/complete`;
    const authToken = await getAuthToken();
    const headers = {
      Authorization: `Bearer ${authToken}`,
    };

    const data = {
      ...payment,
      ...checkoutData,
      terms: checkoutData.terms,
      marketing: checkoutData.marketing,
    };

    const requestOpts: AxiosRequestConfig = {
      data,
      headers,
      method: 'PUT',
      url: url,
    };

    return await axios(requestOpts);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const salesTaxQuotation = async (
  orderId: number | undefined,
  addresses: { zipcode: string },
): Promise<AxiosResponse> => {
  try {
    const url = `${STORE_ROOT}/aapi/v2/orders/${orderId}/quote_tax`;
    const authToken = await getAuthToken();

    const headers = {
      Authorization: `Bearer ${authToken}`,
    };

    const data = {
      addresses: { ...addresses },
    };

    const requestOpts: AxiosRequestConfig = {
      data,
      headers,
      method: 'POST',
      url: url,
    };

    return axios(requestOpts);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const addShippingMethodToOrder = async (
  orderId: number,
  shippingMethodId: string,
): Promise<AxiosResponse> => {
  try {
    const url = `${STORE_ROOT}/aapi/v2/orders/${orderId}/add_shipping`;
    const authToken = await getAuthToken();

    const headers = {
      Authorization: `Bearer ${authToken}`,
    };

    const data = {
      shipping_method_id: shippingMethodId,
    };

    const requestOpts: AxiosRequestConfig = {
      data,
      headers,
      method: 'PUT',
      url: url,
    };

    return axios(requestOpts);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const addGiftCardToOrder = async (
  orderId: number,
  giftCardCode: string,
): Promise<AxiosResponse> => {
  try {
    const url = `${STORE_ROOT}/aapi/v2/orders/${orderId}/apply_gift_card`;
    const authToken = await getAuthToken();

    const headers = {
      Authorization: `Bearer ${authToken}`,
    };

    const data = {
      giftcard_code: giftCardCode,
    };

    const requestOpts: AxiosRequestConfig = {
      data,
      headers,
      method: 'PUT',
      url: url,
    };

    return axios(requestOpts);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const removeGiftCardFromOrder = async (
  orderId: number,
  adjustmentId: number,
): Promise<AxiosResponse> => {
  try {
    const url = `${STORE_ROOT}/aapi/v2/orders/${orderId}/remove_giftup_gift_card`;
    const authToken = await getAuthToken();

    const headers = {
      Authorization: `Bearer ${authToken}`,
    };

    const data = {
      adjustment_id: adjustmentId,
    };

    const requestOpts: AxiosRequestConfig = {
      data,
      headers,
      method: 'PUT',
      url: url,
    };

    return axios(requestOpts);
  } catch (error) {
    return Promise.reject(error);
  }
};

const numbersOnly = (str: string): RegExpMatchArray | null => {
  const onlyNumbers = /[0-9]/;
  return str.match(onlyNumbers);
};

export const formatZipCode = (value: string): string => {
  const input = value.replace(/\D/g, '').substring(0, 9); // First five digits of input only
  const firstFive = input.substring(0, 5);
  const lastFour = input.substring(5, 9);

  if (numbersOnly(input) && input.length > 5) {
    return `${firstFive}-${lastFour}`;
  } else if (numbersOnly(input) && input.length <= 5) {
    return `${firstFive}`;
  } else {
    return '';
  }
};

export const hasSelectedPscLocation = (psc: PSCLocation): boolean =>
  !Object.values(psc).includes('');

export const useQuoteFields = () => {
  const { watch } = useFormContext();

  const watchAddress1 = watch('shipAddress1', '');
  const watchCity = watch('shipCity', '');
  const watchEmail = watch('email', '');
  const watchState = watch('shipState', '');
  const watchZip = watch('shipZipcode', '');

  return {
    watchAddress1,
    watchCity,
    watchEmail,
    watchState,
    watchZip,
  };
};
export const useShippingMethod = (initialShippingMethod: string) => {
  const [shippingMethod, setShippingMethod] = useState(initialShippingMethod);

  useEffect(() => {
    const handleSetShippingMethod = () => {
      const currentShippingMethod = getShippingMethodId();
      setShippingMethod(currentShippingMethod);
    };
    window.addEventListener(
      CART_EVENTS_NAMES.setShippingMethod,
      handleSetShippingMethod,
    );

    return () =>
      window.removeEventListener(
        CART_EVENTS_NAMES.setShippingMethod,
        handleSetShippingMethod,
      );
  }, []);

  return shippingMethod;
};
